import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';

import { Select } from '../base/Select';
import { Button } from '../base/Button';
import { Modal } from '../../layouts/Modal';
import { TextArea } from '../base/TextArea';
import { ActionSelect } from './ActionSelect';
import { TextField } from '../base/TextField';
import { SectionLoading } from '../shared/SectionLoading';
import { IActionProfile, InputType } from '../../types/Action/ActionProfile';
import useSuperAdminActionProfileMutations from '../../data/action/useSuperAdminActionProfileMutation';
import { useGlobalHardwareModelFilter } from '../../contexts/GlobalHardwareFilterContext';
import { GlobalHardwareModelFilterBanner } from '../shared/GlobalHardwareModelFilterBanner';
import { DataPointSelect } from '../DataPoint/DataPointSelect';
import useApplianceTypes from '../../data/appliance/useApplianceTypes';
import { ManufacturerSelect } from '../shared/ManufacturerSelect';
import { ModelSelect } from '../shared/ModelSelect';
import { githubDarkTheme, githubLightTheme, JsonEditor } from 'json-edit-react';
import { useTheme } from '../../contexts/ThemeContext';
import { Toggle } from '../base/Toggle';

export interface CreateEditActionProfileProps {
  open: boolean;
  onClose: Dispatch<SetStateAction<boolean>>;
  actionProfile?: IActionProfile;
}

export function CreateEditActionProfile({
  open,
  onClose,
  actionProfile,
}: CreateEditActionProfileProps) {
  const {
    theme: { isDark },
  } = useTheme();
  const { register, reset, handleSubmit, watch, resetField, setValue } =
    useForm<IActionProfile>({ defaultValues: { step: 1 } });
  const { applianceTypes } = useApplianceTypes();
  const { createActionProfile, updateActionProfile } =
    useSuperAdminActionProfileMutations();
  const { globalHardwareModelFilter } = useGlobalHardwareModelFilter();

  const defaultCommand = {
    property: 'uartData',
    operation: 1,
    payload: { ascii: 'AT+WSAO={{a}},{{v}}' },
  };

  const addDefaultCommand = () => {
    const currentCommands = watch('commands') || [];
    setValue('commands', [...currentCommands, defaultCommand]);
  };

  const handleClose = () => {
    onClose(false);
    setTimeout(() => reset({}), 200);
  };

  const handleCreate = (data: any) => {
    createActionProfile.mutate({
      ...data,
      dataPointId: data.dataPointId === '' ? null : data.dataPointId,
      bit: data.bit !== '' ? data.bit : null,
      inputType: parseInt(data.inputType),
    });
    handleClose();
  };

  const handleUpdate = (data: any) => {
    updateActionProfile.mutate({
      ...actionProfile,
      ...data,
      dataPointId: data.dataPointId === '' ? null : data.dataPointId,
      bit: data.bit !== '' ? data.bit : null,
      inputType: parseInt(data.inputType),
    });
    handleClose();
  };

  const inputTypes = [
    ...Object.values(InputType).filter((ct) => isNaN(Number(ct))),
  ];

  useEffect(() => {
    if (actionProfile) {
      reset({
        hardwareTypeId: actionProfile.hardwareTypeId,
        hardwareManufacturerId: actionProfile.hardwareManufacturerId,
        hardwareModelId: actionProfile.hardwareModelId,
        actionId: actionProfile.actionId,
        dataPointId: actionProfile.dataPointId,
        hardwareModel: actionProfile.hardwareModel,
        commands: actionProfile.commands,
        responseLength: actionProfile.responseLength,
        responseTimeout: actionProfile.responseTimeout,
        inputType: actionProfile.inputType,
        step: actionProfile.step,
      });
    } else if (globalHardwareModelFilter) {
      reset({
        hardwareModelId: globalHardwareModelFilter.id,
        hardwareManufacturerId:
          globalHardwareModelFilter.hardwareManufacturerId,
        hardwareTypeId: globalHardwareModelFilter.hardwareTypeId,
        commands: [],
        responseTimeout: 0,
        responseLength: 0,
        step: 1,
      });
    } else {
      reset({
        commands: [],
        responseTimeout: 0,
        responseLength: 0,
        step: 1,
      });
    }
  }, [
    reset,
    open,
    actionProfile,
    applianceTypes.data,
    applianceTypes.isLoading,
    globalHardwareModelFilter,
  ]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={`${actionProfile ? 'Edit' : 'Create'} Action Profile`}
      description={`Use this form to ${
        actionProfile ? 'edit' : 'create'
      } an action profile`}
    >
      <GlobalHardwareModelFilterBanner />
      {applianceTypes.isLoading ? (
        <SectionLoading />
      ) : (
        <form
          onSubmit={handleSubmit(actionProfile ? handleUpdate : handleCreate)}
          className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-y-4 sm:gap-6 sm:gap-y-4"
        >
          <Select
            {...register('hardwareTypeId', {
              value: '',
              onChange: () => {
                resetField('hardwareManufacturerId');
                resetField('hardwareModelId');
                resetField('actionId');
              },
            })}
            label="Type"
          >
            <option disabled hidden value="">
              Please Select
            </option>
            {applianceTypes.data?.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </Select>
          <ManufacturerSelect
            {...register('hardwareManufacturerId', {
              onChange: () => {
                resetField('hardwareModelId');
                resetField('actionId');
              },
            })}
            type={watch('hardwareTypeId')}
          />
          <ModelSelect
            {...register('hardwareModelId', {
              onChange: () => {
                resetField('actionId');
              },
            })}
            manufacturer={watch('hardwareManufacturerId')}
            type={watch('hardwareTypeId')}
          />
          <ActionSelect
            {...register('actionId')}
            type={watch('hardwareTypeId')}
            model={watch('hardwareModelId')}
            selected={actionProfile?.actionId}
            filter
          />
          <DataPointSelect
            {...register('dataPointId')}
            type={watch('hardwareTypeId')}
            model={watch('hardwareModelId')}
            selected={actionProfile?.dataPointId}
            filterForActionProfiles
          />
          <div className="col-span-2">
            <Button type="button" onClick={addDefaultCommand} className="my-4">
              Add Command
            </Button>
            <JsonEditor
              data={watch('commands')}
              setData={(value) => setValue('commands', value as object[])}
              theme={isDark ? githubDarkTheme : githubLightTheme}
              minWidth="100%"
              className="border border-primary"
            />
          </div>
          <TextField {...register('responseLength')} label="Response Length" />
          <TextField
            {...register('responseTimeout')}
            label="Response Timeout"
          />
          <Select label="Input Type" {...register('inputType')}>
            {inputTypes.map((type, i) => (
              <option key={type} value={i}>
                {String(type)}
              </option>
            ))}
          </Select>
          <TextField {...register('step')} label="Step" step="any" />
          <div className="mt-6 sm:col-span-2 space-x-4">
            <Button type="submit">{actionProfile ? 'Update' : 'Create'}</Button>
            <Button variant="outlined" onClick={handleClose}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
